import { getStore } from '@/base/store/storeRegistry'

/**
 * Klucz w `history.state` wpisu `/payer-paywall`, pod którym leży trasa wejścia (Start/Continue).
 * Trasa `/payer-paywall` nie ma identyfikatorów w adresie, a magazyn po odświeżeniu jest pusty -
 * stan historii przeżywa odświeżenie i Wstecz/Dalej, więc to jedyne miejsce, z którego da się
 * odtworzyć, skąd użytkownik przyszedł (PEP-1323).
 */
const RETURN_STATE_KEY = 'payerPaywallReturn'

/**
 * Trasy wejścia i ich wymagane parametry. Stan historii czytamy jak dane z zewnątrz - przepuszczamy
 * wyłącznie znane trasy z kompletem niepustych parametrów tekstowych.
 */
const RETURN_ROUTE_PARAMS = {
  Start: ['apiToken'],
  Continue: ['idTransRemote', 'continueHash'],
}

/**
 * @param {unknown} value
 * @returns {{ name: string, params: Record<string, string> } | null}
 */
function sanitizeReturnRoute(value) {
  const requiredParams = RETURN_ROUTE_PARAMS[value?.name]
  if (!requiredParams) {
    return null
  }
  const params = {}
  for (const key of requiredParams) {
    const param = value.params?.[key]
    if (typeof param !== 'string' || param === '') {
      return null
    }
    params[key] = param
  }
  return { name: value.name, params }
}

/**
 * Lokalizacja do `router.replace` z widoku wejścia na payer-paywall. Kopiuje trasę powrotu
 * do `history.state` jako zwykły obiekt - reaktywne proxy z Vuex nie przechodzi przez
 * structured clone (`DataCloneError`).
 *
 * @param {unknown} payerPaywallReturn - `state.payment.payerPaywallReturn`
 * @returns {import('vue-router').RouteLocationRaw}
 */
export function payerPaywallLocation(payerPaywallReturn) {
  const returnRoute = sanitizeReturnRoute(payerPaywallReturn)
  return returnRoute
    ? { name: 'PayerPaywall', state: { [RETURN_STATE_KEY]: returnRoute } }
    : { name: 'PayerPaywall' }
}

/**
 * Guard trasy /payer-paywall (beforeEnter).
 * Uruchamia się przy każdej nawigacji do widoku, w tym przy Wstecz/Dalej (popstate),
 * zanim komponent się zamontuje.
 *
 * Zabezpiecza przed:
 *  - wejściem bez danych transakcji: odświeżenie -> powrót na trasę wejścia z `history.state`
 *    (ona pobierze transakcję i przekieruje tu ponownie), deep-link bez stanu -> ErrorNotFound,
 *  - powrotem na formularz po jego zatwierdzeniu (Back po submicie) -> powrót do paywalla,
 *  - wejściem na payer-paywall dla transakcji bez flagi isPayerPaywall -> powrót do paywalla.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} from
 * @param {import('vue-router').NavigationGuardNext} next
 */
export function payerPaywallGuard(to, from, next) {
  const state = getStore().state.payment
  const fallback = state.payerPaywallReturn ?? { name: 'Start', params: { apiToken: state.apiToken } }

  // 1) brak danych: odświeżenie -> trasa wejścia ze stanu historii; deep-link -> nie ma czego wznawiać
  if (!state.paymentData) {
    const storedReturn = sanitizeReturnRoute(window.history.state?.[RETURN_STATE_KEY])
    return next(storedReturn ?? { name: 'ErrorNotFound' })
  }
  // 2) krok już ukończony (Back po submicie) -> do przodu, na paywall
  if (state.payerPaywallCompleted) {
    return next(fallback)
  }
  // 3) transakcja bez flagi payer-paywall -> nie powinno tu trafić
  if (!state.paymentData.isPayerPaywall) {
    return next(fallback)
  }
  return next()
}
